// Reconciliation — payment reconciliation summaries and trigger
import { useState, useCallback, useMemo } from "react";
import {
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Download,
  Flag,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useReconciliation,
  useTriggerReconciliation,
  useExportReconciliationCsv,
} from "@/hooks/admin/usePayments";
import type { ReconciliationSummary } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Reconciliation() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [triggerDate, setTriggerDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  const params = useMemo(
    () => ({
      date_from: dateFrom || null,
      date_to: dateTo || null,
      provider: providerFilter || null,
    }),
    [dateFrom, dateTo, providerFilter],
  );

  const { data, isLoading, isError, refetch } = useReconciliation(params);
  const triggerReconciliation = useTriggerReconciliation();
  const exportCsv = useExportReconciliationCsv();

  const summaries = data?.summaries ?? [];

  const totalDiscrepancy = useMemo(
    () => summaries.reduce((sum, s) => sum + s.discrepancy_fcfa, 0),
    [summaries],
  );

  const totalUnmatched = useMemo(
    () => summaries.reduce((sum, s) => sum + s.unmatched, 0),
    [summaries],
  );

  const handleTrigger = useCallback(() => {
    triggerReconciliation.mutate(triggerDate);
  }, [triggerDate, triggerReconciliation]);

  const handleExportCsv = useCallback(() => {
    exportCsv.mutate(params, {
      onSuccess: (result) => {
        if (result.csv_url) {
          window.open(result.csv_url, "_blank");
        }
      },
    });
  }, [params, exportCsv]);

  // Client-side CSV fallback generation
  const handleLocalExport = useCallback(() => {
    if (summaries.length === 0) return;
    const headers = [
      "Date",
      "Fournisseur",
      "Transactions",
      "Montant total (FCFA)",
      "Frais (FCFA)",
      "Net (FCFA)",
      "Rapprochées",
      "Non rapprochées",
      "Écart (FCFA)",
    ];
    const rows = summaries.map((s) => [
      s.date,
      s.provider,
      s.total_transactions,
      s.total_amount_fcfa,
      s.total_fees_fcfa,
      s.total_net_fcfa,
      s.matched,
      s.unmatched,
      s.discrepancy_fcfa,
    ]);
    const csv = [headers.join(";"), ...rows.map((r) => r.join(";"))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reconciliation-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [summaries]);

  const discrepancyCount = useMemo(
    () => summaries.filter((s) => s.discrepancy_fcfa !== 0).length,
    [summaries],
  );

  const columns: readonly ColumnDef<ReconciliationSummary>[] = useMemo(
    () => [
      {
        id: "date",
        header: "Date",
        cell: (s) => (
          <span className="text-sm font-medium text-stone-900">
            {formatDate(s.date)}
          </span>
        ),
      },
      {
        id: "provider",
        header: "Fournisseur",
        cell: (s) => (
          <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-700">
            {s.provider}
          </span>
        ),
      },
      {
        id: "total_transactions",
        header: "Transactions",
        cell: (s) => (
          <span className="text-sm text-stone-700">{s.total_transactions}</span>
        ),
      },
      {
        id: "total_amount_fcfa",
        header: "Montant total",
        cell: (s) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(s.total_amount_fcfa)}
          </span>
        ),
      },
      {
        id: "total_fees_fcfa",
        header: "Frais",
        cell: (s) => (
          <span className="text-sm text-stone-500">
            {formatFcfa(s.total_fees_fcfa)}
          </span>
        ),
      },
      {
        id: "total_net_fcfa",
        header: "Net",
        cell: (s) => (
          <span className="text-sm font-medium text-emerald-700">
            {formatFcfa(s.total_net_fcfa)}
          </span>
        ),
      },
      {
        id: "matched",
        header: "Rapprochées",
        cell: (s) => (
          <div className="flex items-center gap-1">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
            <span className="text-sm text-emerald-700">{s.matched}</span>
          </div>
        ),
      },
      {
        id: "unmatched",
        header: "Non rapprochées",
        cell: (s) => (
          <div className="flex items-center gap-1">
            {s.unmatched > 0 ? (
              <>
                <XCircle className="h-3.5 w-3.5 text-red-600" />
                <span className="text-sm font-medium text-red-700">
                  {s.unmatched}
                </span>
              </>
            ) : (
              <span className="text-sm text-stone-500">0</span>
            )}
          </div>
        ),
      },
      {
        id: "discrepancy_fcfa",
        header: "Écart (FCFA)",
        cell: (s) => (
          <span
            className={`text-sm font-semibold ${
              s.discrepancy_fcfa !== 0
                ? "text-red-700"
                : "text-stone-500"
            }`}
          >
            {s.discrepancy_fcfa !== 0
              ? formatFcfa(s.discrepancy_fcfa)
              : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="cod-payments">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <RefreshCw className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Réconciliation
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${summaries.length} résumé(s) de réconciliation`}
              </p>
            </div>
          </div>

          {/* Trigger panel + export */}
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm font-medium text-stone-700">Date :</label>
            <input
              type="date"
              value={triggerDate}
              onChange={(e) => setTriggerDate(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
            <Button
              onClick={handleTrigger}
              disabled={triggerReconciliation.isPending}
              className="gap-1.5"
            >
              {triggerReconciliation.isPending ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Déclencher
            </Button>
            <Button
              variant="outline"
              onClick={handleLocalExport}
              disabled={summaries.length === 0}
              className="gap-1.5"
            >
              <Download className="h-4 w-4" />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KPICard
            label="Total écart"
            value={totalDiscrepancy}
            format="currency"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Non rapprochées"
            value={totalUnmatched}
            format="number"
            icon={<XCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Avec écarts"
            value={discrepancyCount}
            format="number"
            icon={<Flag className="h-4 w-4" />}
          />
          <KPICard
            label="Réconciliations"
            value={summaries.length}
            format="number"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Total transactions"
            value={summaries.reduce((s, r) => s + r.total_transactions, 0)}
            format="number"
            icon={<RefreshCw className="h-4 w-4" />}
          />
        </div>

        {/* Success/error notification for trigger */}
        {triggerReconciliation.isSuccess && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span>Réconciliation déclenchée avec succès.</span>
          </div>
        )}
        {triggerReconciliation.isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de déclencher la réconciliation.</span>
          </div>
        )}

        {/* Filters: date range + provider */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les fournisseurs</option>
            <option value="wave">Wave</option>
            <option value="orange_money">Orange Money</option>
            <option value="cash_on_delivery">Espèces / COD</option>
          </select>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-stone-700">Du :</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-stone-700">Au :</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          {(dateFrom || dateTo || providerFilter) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
                setProviderFilter("");
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les données de réconciliation.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && summaries.length === 0 ? (
              <EmptyState
                icon={<RefreshCw className="h-6 w-6" />}
                title="Aucune réconciliation"
                description="Déclenchez une réconciliation pour commencer."
              />
            ) : (
              <AdminDataTable
                data={summaries}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>
      </ModuleGuard>
    </AdminLayout>
  );
}
